import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Input, Select, Button } from '../../../components/shared/StyledComponents'
import { FeedFilters, FeedSort, LinkedInFacets } from '../../../services/api'

/**
 * Filters for the content list.
 *
 * Every control here maps to something the backend actually applies, and which
 * controls are shown depends on what is being listed — the list has one entry
 * type at a time, so a filter that does not apply to it is not rendered rather
 * than rendered and ignored.
 *
 * The per-tab variants this used to carry are gone with the tabs. One of them
 * really was dead: `language` was sent to the post query, which filters
 * `linkedin_posts.language` — a column nothing in the pipeline writes — using the
 * *programming* languages the facets endpoint reads from `linkedin_repos`. It is
 * now offered only where it means something, which is the repo list.
 */

const Bar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
`

const Actions = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  grid-column: 1 / -1;
  gap: ${({ theme }) => theme.spacing.sm};
`

/**
 * What the list is showing.
 *
 * Publications are the default and the point of the page: one entry per piece of
 * content, badged with what it carries. The other two are not a return of the old
 * tabs — they are how a repo or a paper gets *found*. "¿Qué posts mencionan
 * pytorch/vision?", "¿qué repos se están moviendo?" and "¿qué papers no vienen de
 * ninguna publicación?" cannot be asked of a list of posts, and the last one
 * names rows the post list cannot reach at all.
 */
export type FeedEntity = 'posts' | 'repos' | 'papers'

const ENTITY_OPTIONS: { value: FeedEntity; label: string }[] = [
    { value: 'posts', label: '📝 Publicaciones' },
    { value: 'repos', label: '💻 Repositorios' },
    { value: 'papers', label: '📄 Papers' },
]

/** What a piece of content carries. The pair of booleans behind each option is
 *  what the backend expects; `undefined` means "don't filter on this". */
export type CarryFilter = 'all' | 'repo' | 'paper' | 'both' | 'none'

const CARRY_OPTIONS: { value: CarryFilter; label: string; has_repo?: boolean; has_paper?: boolean }[] = [
    { value: 'all', label: 'Todo' },
    { value: 'repo', label: 'Con repositorio', has_repo: true },
    { value: 'paper', label: 'Con paper', has_paper: true },
    { value: 'both', label: 'Con repositorio y paper', has_repo: true, has_paper: true },
    { value: 'none', label: 'Sin repo ni paper', has_repo: false, has_paper: false },
]

/** Read the selected option back out of the filters, so the control still shows
 *  the truth after "Limpiar" or a reload. */
export const carryValueOf = (filters: FeedFilters): CarryFilter => {
    const { has_repo, has_paper } = filters
    if (has_repo === false && has_paper === false) return 'none'
    if (has_repo && has_paper) return 'both'
    if (has_repo) return 'repo'
    if (has_paper) return 'paper'
    return 'all'
}

/** Same idea for the papers list: absent, yes and no are three answers. */
export const postedValueOf = (filters: FeedFilters): string => {
    if (filters.has_post === true) return 'yes'
    if (filters.has_post === false) return 'no'
    return 'all'
}

interface FeedFilterBarProps {
    filters: FeedFilters
    entity: FeedEntity
    facets: LinkedInFacets | null
    onEntityChange: (entity: FeedEntity) => void
    /** Called with patched filters (debounced search is handled internally). */
    onChange: (patch: Partial<FeedFilters>) => void
    onClear: () => void
}

export const FeedFilterBar: React.FC<FeedFilterBarProps> = ({
    filters,
    entity,
    facets,
    onEntityChange,
    onChange,
    onClear,
}) => {
    // Local search text for debouncing.
    const [searchText, setSearchText] = useState(filters.search || '')

    // Keep local search in sync when filters are cleared externally.
    useEffect(() => {
        setSearchText(filters.search || '')
    }, [filters.search])

    // Debounce search → propagate after 350ms of inactivity.
    useEffect(() => {
        const handle = setTimeout(() => {
            if ((filters.search || '') !== searchText) {
                onChange({ search: searchText || undefined })
            }
        }, 350)
        return () => clearTimeout(handle)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText])

    const searchPlaceholder = {
        posts: 'Texto, resumen o autor…',
        repos: 'Owner, nombre o descripción…',
        papers: 'Título del paper…',
    }[entity]

    return (
        <Bar>
            <Field>
                <FieldLabel htmlFor="feed-entity">Listar</FieldLabel>
                <Select
                    id="feed-entity"
                    value={entity}
                    onChange={(e) => onEntityChange(e.target.value as FeedEntity)}
                >
                    {ENTITY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </Select>
            </Field>

            <Field style={{ gridColumn: 'span 2' }}>
                <FieldLabel htmlFor="feed-search">Buscar</FieldLabel>
                <Input
                    id="feed-search"
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel htmlFor="feed-sort">Ordenar por</FieldLabel>
                <Select
                    id="feed-sort"
                    value={filters.sort || 'recent'}
                    onChange={(e) => onChange({ sort: e.target.value as FeedSort })}
                >
                    <option value="recent">Más recientes</option>
                    {entity === 'posts' && <option value="stars">Más reacciones</option>}
                    {entity === 'posts' && <option value="signal">Mayor relevancia</option>}
                    {entity === 'repos' && <option value="stars">Más estrellas</option>}
                    {entity === 'repos' && <option value="trending">En tendencia</option>}
                </Select>
            </Field>

            {entity === 'posts' && (
                <>
                    <Field>
                        <FieldLabel htmlFor="feed-carry">Contenido</FieldLabel>
                        <Select
                            id="feed-carry"
                            value={carryValueOf(filters)}
                            onChange={(e) => {
                                const opt = CARRY_OPTIONS.find((o) => o.value === e.target.value) || CARRY_OPTIONS[0]
                                onChange({ has_repo: opt.has_repo, has_paper: opt.has_paper })
                            }}
                        >
                            {CARRY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-category">Categoría</FieldLabel>
                        <Select
                            id="feed-category"
                            value={filters.category || ''}
                            onChange={(e) => onChange({ category: e.target.value || undefined })}
                        >
                            <option value="">Todas</option>
                            {(facets?.categories || []).map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-tag">Etiqueta</FieldLabel>
                        <Select
                            id="feed-tag"
                            value={filters.tag || ''}
                            onChange={(e) => onChange({ tag: e.target.value || undefined })}
                        >
                            <option value="">Todas</option>
                            {(facets?.tags || []).map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </Select>
                    </Field>
                </>
            )}

            {entity === 'repos' && (
                <>
                    <Field>
                        <FieldLabel htmlFor="feed-language">Lenguaje</FieldLabel>
                        <Select
                            id="feed-language"
                            value={filters.language || ''}
                            onChange={(e) => onChange({ language: e.target.value || undefined })}
                        >
                            <option value="">Todos</option>
                            {(facets?.languages || []).map((l) => (
                                <option key={l} value={l}>{l}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-min-stars">Estrellas mínimas</FieldLabel>
                        <Input
                            id="feed-min-stars"
                            type="number"
                            min={0}
                            placeholder="0"
                            value={filters.min_stars ?? ''}
                            onChange={(e) =>
                                onChange({ min_stars: e.target.value === '' ? undefined : Number(e.target.value) })
                            }
                        />
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-trending">Movimiento</FieldLabel>
                        <Select
                            id="feed-trending"
                            value={filters.trending === true ? 'yes' : 'all'}
                            onChange={(e) => onChange({ trending: e.target.value === 'yes' ? true : undefined })}
                        >
                            <option value="all">Todos</option>
                            <option value="yes">Solo en tendencia</option>
                        </Select>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-inactive">Eliminados en GitHub</FieldLabel>
                        <Select
                            id="feed-inactive"
                            value={filters.include_inactive ? 'yes' : 'no'}
                            onChange={(e) =>
                                onChange({ include_inactive: e.target.value === 'yes' ? true : undefined })
                            }
                        >
                            <option value="no">Ocultar</option>
                            <option value="yes">Incluir</option>
                        </Select>
                    </Field>
                </>
            )}

            {entity === 'papers' && (
                <>
                    <Field>
                        <FieldLabel htmlFor="feed-source">Fuente</FieldLabel>
                        <Select
                            id="feed-source"
                            value={filters.source || ''}
                            onChange={(e) => onChange({ source: e.target.value || undefined })}
                        >
                            <option value="">Todas</option>
                            {(facets?.sources || []).map((src) => (
                                <option key={src} value={src}>{src}</option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="feed-posted">Origen</FieldLabel>
                        <Select
                            id="feed-posted"
                            value={postedValueOf(filters)}
                            onChange={(e) => {
                                const value = e.target.value
                                onChange({ has_post: value === 'all' ? undefined : value === 'yes' })
                            }}
                        >
                            <option value="all">Todos</option>
                            <option value="yes">De una publicación</option>
                            {/* The only way to reach the papers the repo agent
                                extracted from a repo's README: no post mentions
                                them, so the publication list cannot show them. */}
                            <option value="no">Sin publicación</option>
                        </Select>
                    </Field>
                </>
            )}

            <Field>
                <FieldLabel htmlFor="feed-from">Desde</FieldLabel>
                <Input
                    id="feed-from"
                    type="date"
                    value={filters.date_from || ''}
                    onChange={(e) => onChange({ date_from: e.target.value || undefined })}
                />
            </Field>

            <Field>
                <FieldLabel htmlFor="feed-to">Hasta</FieldLabel>
                <Input
                    id="feed-to"
                    type="date"
                    value={filters.date_to || ''}
                    onChange={(e) => onChange({ date_to: e.target.value || undefined })}
                />
            </Field>

            <Actions>
                <Button variant="secondary" size="sm" onClick={onClear}>
                    Limpiar
                </Button>
            </Actions>
        </Bar>
    )
}

export default FeedFilterBar
